import React, { useEffect, useState } from "react";

// Enhanced Segment Detail Modal Component with Audience Data
export const EnhancedSegmentDetailModal = ({
	segment,
	isOpen,
	onClose,
	categoryDataMap = null,
}) => {
	const [activeTab, setActiveTab] = useState("performance");
	const [audienceData, setAudienceData] = useState(null);
	const [audienceLoading, setAudienceLoading] = useState(false);
	const [audienceError, setAudienceError] = useState(null);

	// Fetch audience data when modal opens
	useEffect(() => {
		const fetchAudienceData = async () => {
			if (!isOpen || !segment?.segmentVector?.segmentId) {
				return;
			}

			const segmentId = segment.segmentVector.segmentId;
			console.log(`🎯 Fetching audience data for segment: ${segmentId}`);

			setAudienceLoading(true);
			setAudienceError(null);

			try {
				const apiService = window.segmentsApiService;
				if (!apiService) {
					throw new Error("Segments API Service not available");
				}

				const response = await apiService.getSegmentAudienceData(segmentId);

				if (response.success) {
					console.log("✅ Audience data fetched successfully:", response.data);
					setAudienceData(response.data);
				} else {
					console.error("❌ Failed to fetch audience data:", response.error);
					setAudienceError(response.error || "Failed to load audience data");
				}
			} catch (error) {
				console.error("❌ Error fetching audience data:", error);
				setAudienceError(
					error.message || "An error occurred while loading audience data",
				);
			} finally {
				setAudienceLoading(false);
			}
		};

		fetchAudienceData();
	}, [isOpen, segment?.segmentVector?.segmentId]);

	// Reset states when modal closes
	useEffect(() => {
		if (!isOpen) {
			setAudienceData(null);
			setAudienceError(null);
			setAudienceLoading(false);
			setActiveTab("performance");
		}
	}, [isOpen]);

	if (!isOpen || !segment) return null;

	const formatNumber = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		const num = Number(value) || 0;
		return num.toLocaleString();
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		const num = Number(value) || 0;
		if (num >= 1000000000) return `$${(num / 1000000000).toFixed(1)}B`;
		if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
		return `$${num.toFixed(2)}`;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		if (typeof value === "number") {
			return `${(value).toFixed(5)}%`;
		}
		return value;
	};

	const formatNumberWithCommas = (value) => {
		if (value === null || value === undefined || value === "") return "N/A";
		const num = Number(value) || 0;
		if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
		if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
		return num.toLocaleString();
	};

	const getSegmentType = () => {
		const segmentId = segment.segmentVector?.segmentId;

		if (segmentId && categoryDataMap) {
			const categoryType = categoryDataMap[segmentId];
			if (categoryType && (categoryType === "OA" || categoryType === "OAA")) {
				return categoryType;
			}
		}

		const directCategory = segment.segmentVector?.segmentCategory;
		if (directCategory) {
			const upperCategory = directCategory.toUpperCase();
			if (upperCategory.includes("OA") && !upperCategory.includes("OAA")) {
				return "OA";
			} else if (upperCategory.includes("OAA")) {
				return "OAA";
			}
		}

		const category = segment.segmentVector?.category;
		const subCategory = segment.segmentVector?.subCategory;

		if (category === "predefined") {
			if (
				[
					"third_party_data",
					"past_browsing_interests",
					"partner_audiences",
				].includes(subCategory)
			) {
				return "OA";
			} else if (subCategory === "bulk") {
				return "OAA";
			}
		}

		if (category === "direct" && subCategory === "bulk") {
			return "OAA";
		}

		return "OAA";
	};

	const tabs = [
		{ id: "performance", label: "Performance & Scale", icon: "📊" },
		{ id: "metadata", label: "Segment Details", icon: "🎯" },
		{ id: "audience", label: "Audience Data", icon: "👥" },
	];

	const renderAudienceTab = () => {
		if (audienceLoading) {
			return (
				<div className="tab-content">
					<div className="loading-section">
						<div className="loading-spinner"></div>
						<p>Loading audience data...</p>
					</div>
				</div>
			);
		}

		if (audienceError) {
			return (
				<div className="tab-content">
					<div className="error-section">
						<div className="error-icon">⚠️</div>
						<h4>Failed to Load Audience Data</h4>
						<p>{audienceError}</p>
						<button
							className="retry-button"
							onClick={() => {
								setAudienceError(null);
								const segmentId = segment.segmentVector?.segmentId;
								if (segmentId && window.segmentsApiService) {
									setAudienceLoading(true);
									window.segmentsApiService
										.getSegmentAudienceData(segmentId)
										.then((response) => {
											if (response.success) {
												setAudienceData(response.data);
											} else {
												setAudienceError(
													response.error || "Failed to load audience data",
												);
											}
										})
										.catch((error) => {
											setAudienceError(
												error.message ||
													"An error occurred while loading audience data",
											);
										})
										.finally(() => {
											setAudienceLoading(false);
										});
								}
							}}
						>
							Retry
						</button>
					</div>
				</div>
			);
		}

		if (
			!audienceData ||
			!Array.isArray(audienceData) ||
			audienceData.length === 0
		) {
			return (
				<div className="tab-content">
					<div className="no-data-section">
						<div className="no-data-icon">🔭</div>
						<h4>No Audience Data Available</h4>
						<p>No audience information found for this segment.</p>
					</div>
				</div>
			);
		}

		const totalAudiences = audienceData.length;
		const totalUniqueUsers = audienceData.reduce(
			(sum, aud) => sum + (aud.uniqueUsers || 0),
			0,
		);
		const totalAddressableReach = audienceData.reduce(
			(sum, aud) => sum + (aud.addressableReach || 0),
			0,
		);
		const avgEcpm =
			audienceData.reduce((sum, aud) => sum + (aud.avgCpm || 0), 0) /
			audienceData.length;
		const totalSpend = audienceData.reduce(
			(sum, aud) => sum + (aud.spendInUsd || 0),
			0,
		);
		const totalImpressions = audienceData.reduce(
			(sum, aud) => sum + (aud.weightedImpressions || 0),
			0,
		);

		const getStatusClass = (status) => {
			switch (status?.toUpperCase()) {
				case "PROCESSED":
				case "COMPLETE":
				case "ACTIVE":
					return "status-success";
				case "PROCESSING":
				case "PENDING":
					return "status-pending";
				case "ERROR":
				case "FAILED":
					return "status-error";
				default:
					return "status-unknown";
			}
		};

		return (
			<div className="tab-content">
				<div className="section">
					<h4>Audience Overview</h4>
					<div className="audience-summary">
						<div className="summary-grid">
							<div className="summary-card">
								<div className="summary-value">{totalAudiences}</div>
								<div className="summary-label">Total Audiences</div>
							</div>
							<div className="summary-card">
								<div className="summary-value">
									{formatNumberWithCommas(totalUniqueUsers)}
								</div>
								<div className="summary-label">Total Unique Users</div>
							</div>
							<div className="summary-card">
								<div className="summary-value">
									{formatNumberWithCommas(totalAddressableReach)}
								</div>
								<div className="summary-label">Total Addressable Reach</div>
							</div>
							<div className="summary-card">
								<div className="summary-value">
									{formatNumberWithCommas(totalImpressions)}
								</div>
								<div className="summary-label">Total Impressions</div>
							</div>
							<div className="summary-card">
								<div className="summary-value">
									{formatCurrency(totalSpend / 1000000)}
								</div>
								<div className="summary-label">Total Spend</div>
							</div>
							<div className="summary-card">
								<div className="summary-value">${avgEcpm.toFixed(2)}</div>
								<div className="summary-label">Avg eCPM</div>
							</div>
						</div>
					</div>
				</div>

				<div className="section">
					<h4>Audience Details</h4>
					<div className="audience-table-container">
						<table className="audience-table">
							<thead>
								<tr>
									<th className="audience-name-column">Audience Name</th>
									<th className="audience-status-column">Status</th>
									<th className="audience-provider-column">Provider</th>
									<th className="audience-persona-column">Primary Persona</th>
									<th className="audience-strategy-column">Strategy</th>
									<th className="audience-reach-column">Addressable Reach</th>
									<th className="audience-unique-users-column">Unique users</th>
									<th className="audience-impressions-column">Impressions</th>
									<th className="audience-ecpm-column">eCPM</th>
									<th className="audience-spend-column">Total Spend</th>
									<th className="audience-deals-column">Report Months</th>
								</tr>
							</thead>
							<tbody>
								{audienceData.map((audience, index) => (
									<tr
										key={audience.audienceId || index}
										className="audience-table-row"
									>
										<td className="audience-name-cell">
											<div className="audience-name-content">
												<span className="audience-name">
													{audience.audienceName || `Audience ${index + 1}`}
												</span>
												<span className="audience-account">
													{audience.accountName}
												</span>
											</div>
										</td>
										<td className="audience-status-cell">
											<span
												className={`audience-status-badge ${getStatusClass(audience.status)}`}
											>
												{audience.status || "Unknown"}
											</span>
										</td>
										<td className="audience-provider-cell">
											<div className="provider-content">
												<span className="provider-name">
													{audience.directAudienceProvider || "N/A"}
												</span>
												<span className="data-source">
													{audience.dataSource}
												</span>
											</div>
										</td>
										<td className="audience-persona-cell">
											{audience.primaryPersona || "N/A"}
										</td>
										<td className="audience-strategy-cell">
											{audience.audienceStrategy || "N/A"}
										</td>
										<td className="audience-reach-cell">
											<span className="reach-primary">
												{formatNumberWithCommas(audience.addressableReach || 0)}
											</span>
										</td>
										<td className="audience-unique-users-cell">
											<span className="reach-secondary">
												{formatNumberWithCommas(audience.uniqueUsers || 0)}{" "}
												users
											</span>
										</td>
										<td className="audience-impressions-cell">
											{formatNumberWithCommas(
												audience.weightedImpressions || 0,
											)}
										</td>
										<td className="audience-ecpm-cell">
											${(audience.avgCpm || 0).toFixed(2)}
										</td>
										<td className="audience-spend-cell">
											<div className="spend-content">
												<span className="spend-primary">
													{formatCurrency(audience.spendInUsd / 1000000 || 0)}
												</span>
												<span className="spend-secondary">
													${(audience.avgSpendPerUniqueUser || 0).toFixed(4)}
													/user
												</span>
											</div>
										</td>
										<td className="audience-deals-cell">
											<div className="report-months-inline">
												{audience.reportMonths &&
												audience.reportMonths.length > 0 ? (
													<>
														<span className="months-count">
															{audience.reportMonths.length} months
														</span>
														<div className="months-list">
															{audience.reportMonths
																.slice(0, 4)
																.map((month, idx) => (
																	<span key={idx} className="month-badge">
																		{month}
																	</span>
																))}
															{audience.reportMonths.length > 4 && (
																<span className="more-months">
																	+{audience.reportMonths.length - 4}
																</span>
															)}
														</div>
													</>
												) : (
													"N/A"
												)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		);
	};

	const renderTabContent = () => {
		if (activeTab === "audience") {
			return renderAudienceTab();
		}

		const metrics = segment.segmentMetrics || {};
		const vector = segment.segmentVector || {};

		switch (activeTab) {
			case "performance":
				return (
					<div className="tab-content">
						<div className="section">
							<h4>Key Performance Indicators</h4>
							<div className="metric-grid-5">
								<div className="metric-card">
									<div className="metric-value">
										{formatNumber(metrics.uniqueUsers || vector.reach)}
									</div>
									<div className="metric-label">Reach</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatNumber(metrics.weightedImpressions)}
									</div>
									<div className="metric-label">Impressions</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{audienceData?.length || 0}
									</div>
									<div className="metric-label">No. of Audiences</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatCurrency(metrics.weightedSpendInUsd / 1000000)}
									</div>
									<div className="metric-label">Spend</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{metrics.reportMonths || "N/A"}
									</div>
									<div className="metric-label">Report months</div>
								</div>
							</div>

							<div
								style={{
									marginTop: "12px",
									display: "flex",
									gap: "8px",
									justifyContent: "center",
								}}
							>
								<div className="score-tag">
									<span className="score-label">Composite:</span>
									<span className="score-value">
										{segment.compositeScore?.toFixed(1) || "N/A"}
									</span>
								</div>
								<div className="score-tag">
									<span className="score-label">Search:</span>
									<span className="score-value">
										{segment.score?.toFixed(1) || "N/A"}
									</span>
								</div>
							</div>
						</div>

						<div className="section">
							<h4>Performance Metrics</h4>
							<div className="metric-grid-3">
								<div className="metric-group">
									<div className="group-title">Engagement</div>
									<div className="metric-row">
										<span>Click Rate</span>
										<span>
											{formatPercentage(metrics.weightedClickThroughRate)}
										</span>
									</div>
									<div className="metric-row">
										<span>Viewability</span>
										<span>{formatPercentage(metrics.weightedViewability)}</span>
									</div>
									<div className="metric-row">
										<span>Clicks</span>
										<span>
											{formatNumberWithCommas(metrics.weightedClicks)}
										</span>
									</div>
									<div className="metric-row">
										<span>Video Completions</span>
										<span>
											{formatNumberWithCommas(
												metrics.weightedCompleteFirstOccurrence,
											)}
										</span>
									</div>
								</div>

								<div className="metric-group">
									<div className="group-title">Revenue</div>
									<div className="metric-row">
										<span>Average eCPM</span>
										<span>{formatCurrency(metrics.avgCpm)}</span>
									</div>
									<div className="metric-row">
										<span>eCPM Range</span>
										<span>
											{formatCurrency(metrics.minCpm)} -{" "}
											{formatCurrency(metrics.maxCpm)}
										</span>
									</div>
									<div className="metric-row">
										<span>Spend Per User</span>
										<span>{formatCurrency(metrics.avgSpendPerUniqueUser)}</span>
									</div>
									<div className="metric-row">
										<span>Advertiser Spend</span>
										<span>
											{formatCurrency(metrics.advertiserSpend / 1000000)}
										</span>
									</div>
								</div>

								<div className="metric-group">
									<div className="group-title">Scale</div>
									<div className="metric-row">
										<span>Total Requests</span>
										<span>{formatNumberWithCommas(metrics.requests)}</span>
									</div>
									<div className="metric-row">
										<span>Matched Count</span>
										<span>
											{formatNumberWithCommas(vector.sourceMatchedCount)}
										</span>
									</div>
									<div className="metric-row">
										<span>Cookies</span>
										<span>{formatNumberWithCommas(metrics.cookies)}</span>
									</div>
									<div className="metric-row">
										<span>Fills</span>
										<span>{formatNumberWithCommas(metrics.weightedFills)}</span>
									</div>
								</div>
							</div>
						</div>

						<div className="section">
							<h4>Device Distribution</h4>
							<div className="metric-grid-4">
								<div className="device-card">
									<div className="device-icon">📱</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.mobileDevices)}
									</div>
									<div className="device-label">Mobile</div>
								</div>
								<div className="device-card">
									<div className="device-icon">💻</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.uniqueIpsWithRequests)}
									</div>
									<div className="device-label">Desktop IPs</div>
								</div>
								<div className="device-card">
									<div className="device-icon">📋</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.tabletDevices)}
									</div>
									<div className="device-label">Tablet</div>
								</div>
								<div className="device-card">
									<div className="device-icon">📺</div>
									<div className="device-count">
										{formatNumberWithCommas(metrics.connectedTvDevices)}
									</div>
									<div className="device-label">Connected TV</div>
								</div>
							</div>
						</div>

						<div className="section">
							<h4>Win Rates & Performance</h4>
							<div className="metric-grid-4">
								<div className="metric-card">
									<div className="metric-value">
										{formatPercentage(metrics.weightedOxWinRate * 100)}
									</div>
									<div className="metric-label">OX Win Rate</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatPercentage(metrics.weightedPubWinRate * 100)}
									</div>
									<div className="metric-label">Publisher Win Rate</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatCurrency(metrics.weightedBidCpm / 1000000)}
									</div>
									<div className="metric-label">Bid CPM</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatNumber(metrics.impressionFrequency)}
									</div>
									<div className="metric-label">Impression Frequency</div>
								</div>
							</div>
						</div>
					</div>
				);

			case "metadata":
				return (
					<div className="tab-content">
						<div className="section">
							<h4>Basic Information</h4>
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Segment Name</span>
									<span className="info-value">
										{vector.segmentName || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Account</span>
									<span className="info-value">
										{vector.accountName || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Data Provider</span>
									<span className="info-value">
										{vector.providerId || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Audience Type</span>
									<span className="info-value">
										{vector.audienceType || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Category Type</span>
									<span className="info-value">{getSegmentType()}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Status</span>
									<span className="info-value">{vector.status || "N/A"}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Type</span>
									<span className="info-value">{vector.type || "N/A"}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Sub Type</span>
									<span className="info-value">{vector.subType || "N/A"}</span>
								</div>
								<div className="info-item">
									<span className="info-label">Sellable</span>
									<span className="info-value">
										{vector.sellable ? "Yes" : "No"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Is ABM</span>
									<span className="info-value">
										{vector.isAbm ? "Yes" : "No"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Is Leaf</span>
									<span className="info-value">
										{vector.isLeaf ? "Yes" : "No"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Is Archived</span>
									<span className="info-value">
										{vector.isArchived ? "Yes" : "No"}
									</span>
								</div>
							</div>
							{vector.description && (
								<div className="description">
									<span className="info-label">Description</span>
									<p>{vector.description}</p>
								</div>
							)}
						</div>

						<div className="section">
							<h4>Category & Classification</h4>
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Category L1</span>
									<span className="info-value">
										{vector.categoryL1 || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Category L2</span>
									<span className="info-value">
										{vector.categoryL2 || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Attribute Name</span>
									<span className="info-value">
										{vector.attributeName || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Attribute Value</span>
									<span className="info-value">
										{vector.attributeValue || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Value Qualifier</span>
									<span className="info-value">
										{vector.valueQualifier || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Price CPM</span>
									<span className="info-value">
										{formatCurrency(vector.priceCpm)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Price POM</span>
									<span className="info-value">
										{formatCurrency(vector.pricePom)}
									</span>
								</div>
							</div>
						</div>

						<div className="section">
							<h4>Reach & Count Information</h4>
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Total Reach</span>
									<span className="info-value">
										{formatNumberWithCommas(vector.reach)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Source Provided Count</span>
									<span className="info-value">
										{formatNumberWithCommas(vector.sourceProvidedCount)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Source Matched Count</span>
									<span className="info-value">
										{formatNumberWithCommas(vector.sourceMatchedCount)}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Match Rate</span>
									<span className="info-value">
										{vector.sourceProvidedCount && vector.sourceMatchedCount
											? `${((vector.sourceMatchedCount / vector.sourceProvidedCount) * 100).toFixed(1)}%`
											: "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Households</span>
									<span className="info-value">
										{formatNumberWithCommas(metrics.households)}
									</span>
								</div>
							</div>
						</div>

						<div className="section">
							<h4>Technical Configuration</h4>
							<div className="info-grid">
								<div className="info-item">
									<span className="info-label">Segment ID</span>
									<span className="info-value code">
										{vector.segmentId || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Account ID</span>
									<span className="info-value code">
										{vector.accountId || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">External ID</span>
									<span className="info-value code">
										{vector.externalId || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Provider ID</span>
									<span className="info-value">
										{vector.providerId || "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Date Added</span>
									<span className="info-value">
										{vector.dateAdded
											? new Date(
													vector.dateAdded[0],
													vector.dateAdded[1] - 1,
													vector.dateAdded[2],
												).toLocaleDateString()
											: "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Expiration Date</span>
									<span className="info-value">
										{vector.expirationDate
											? new Date(
													vector.expirationDate[0],
													vector.expirationDate[1] - 1,
													vector.expirationDate[2],
												).toLocaleDateString()
											: "N/A"}
									</span>
								</div>
								<div className="info-item">
									<span className="info-label">Is Deleted In Source</span>
									<span className="info-value">
										{vector.isDeletedInSource ? "Yes" : "No"}
									</span>
								</div>
							</div>
						</div>

						<div className="section">
							<h4>Data Sources & Storage</h4>
							<div className="info-grid">
								<div className="info-item full">
									<span className="info-label">BigQuery Location</span>
									<span className="info-value code">
										{vector.bqLocation || "N/A"}
									</span>
								</div>
								<div className="info-item full">
									<span className="info-label">GCS File Path</span>
									<span className="info-value code">
										{vector.gcsFilePath || "N/A"}
									</span>
								</div>
								<div className="info-item full">
									<span className="info-label">Original Segment Path</span>
									<span className="info-value">
										{vector.originalSegmentPath || "N/A"}
									</span>
								</div>
								<div className="info-item full">
									<span className="info-label">Full Name</span>
									<span className="info-value">{vector.fullName || "N/A"}</span>
								</div>
								{vector.path && vector.path.length > 0 && (
									<div className="info-item full">
										<span className="info-label">Path</span>
										<div className="tag-list">
											{vector.path.map((pathItem, index) => (
												<span key={index} className="tag">
													{pathItem}
												</span>
											))}
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="section">
							<h4>Geographic & Targeting</h4>
							<div className="info-grid">
								<div className="info-item full">
									<span className="info-label">Allowed Countries</span>
									{vector.allowedCountries &&
									vector.allowedCountries.length > 0 ? (
										<div className="tag-list">
											{vector.allowedCountries.map((country, index) => (
												<span key={index} className="tag">
													{country}
												</span>
											))}
										</div>
									) : (
										<span className="info-value">N/A</span>
									)}
								</div>
							</div>
						</div>

						{vector.sourceBqTables &&
							vector.sourceBqTables.length > 0 && (
								<div className="section">
									<h4>Source BigQuery Tables</h4>
									<div className="info-grid">
										{vector.sourceBqTables.map(({ key, value }) => (
											<div key={key} className="info-item full">
												<span className="info-label">
													{key?.toUpperCase()}
												</span>
												<span className="info-value code">{value}</span>
											</div>
										))}
									</div>
								</div>
							)}

						<div className="section">
							<h4>Technical Performance</h4>
							<div className="metric-grid-4">
								<div className="metric-card">
									<div className="metric-value">
										{formatNumberWithCommas(metrics.weightedNonZeroBids)}
									</div>
									<div className="metric-label">Non-Zero Bids</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatCurrency(metrics.weightedNonZeroBidsValue / 1000000)}
									</div>
									<div className="metric-label">Bid Value</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatNumberWithCommas(metrics.uniqueIpsWithRequests)}
									</div>
									<div className="metric-label">Unique IPs</div>
								</div>
								<div className="metric-card">
									<div className="metric-value">
										{formatPercentage(metrics.impressionFrequency)}
									</div>
									<div className="metric-label">Impression Frequency</div>
								</div>
							</div>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="modal-container" onClick={(e) => e.stopPropagation()}>
				<div className="modal-header">
					<div className="header-content">
						<div>
							<h2 className="modal-title">
								{segment.segmentVector?.segmentName || "Unknown Segment"}
							</h2>
							<p className="modal-subtitle">
								{segment.segmentVector?.accountName} •{" "}
								{segment.segmentVector?.providerId}
							</p>
						</div>
						<button onClick={onClose} className="close-button">
							×
						</button>
					</div>
				</div>

				<div className="tab-navigation">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							onClick={() => setActiveTab(tab.id)}
							className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
						>
							<span className="tab-icon">{tab.icon}</span>
							<span className="tab-label">{tab.label}</span>
							{tab.id === "audience" && audienceLoading && (
								<span className="tab-loading">⏳</span>
							)}
						</button>
					))}
				</div>

				<div className="modal-body">{renderTabContent()}</div>

				<style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .modal-container {
            background: white;
            border-radius: 12px;
            width: 900px;
            height: 650px;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
            overflow: hidden;
          }

          .modal-header {
            background: #f8f9fa;
            border-bottom: 1px solid #e9ecef;
            padding: 20px 24px;
          }

          .header-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .modal-title {
            margin: 0 0 6px 0;
            font-size: 20px;
            font-weight: 600;
            color: #212529;
            line-height: 1.3;
          }

          .modal-subtitle {
            margin: 0;
            font-size: 14px;
            color: #6c757d;
          }

          .close-button {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            color: #6c757d;
            font-size: 20px;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
          }

          .close-button:hover {
            background: #e9ecef;
            color: #495057;
          }

          .tab-navigation {
            display: flex;
            background: #f8f9fa;
            border-bottom: 1px solid #e9ecef;
          }

          .tab-button {
            flex: 1;
            padding: 14px 16px;
            border: none;
            background: transparent;
            color: #6c757d;
            font-weight: 500;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s ease;
            border-bottom: 2px solid transparent;
          }

          .tab-button:hover {
            background: #f0f0f0;
            color: #495057;
          }

          .tab-button.active {
            background: white;
            color: #495057;
            border-bottom-color: #495057;
            font-weight: 600;
          }

          .tab-icon {
            font-size: 16px;
          }

          .tab-loading {
            font-size: 12px;
            margin-left: 4px;
          }

          .modal-body {
            flex: 1;
            overflow-y: auto;
          }

          .tab-content {
            padding: 20px 24px;
          }

          .section {
            margin-bottom: 24px;
          }

          .section:last-child {
            margin-bottom: 0;
          }

          .section h4 {
            margin: 0 0 12px 0;
            font-size: 15px;
            font-weight: 600;
            color: #495057;
            padding-bottom: 6px;
            border-bottom: 1px solid #e9ecef;
          }

          .score-tag {
            background: #e3f2fd;
            border: 1px solid #bbdefb;
            border-radius: 16px;
            padding: 4px 12px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
          }

          .score-label {
            color: #1565c0;
            font-weight: 600;
          }

          .score-value {
            color: #0d47a1;
            font-weight: 700;
          }

          .loading-section, .error-section, .no-data-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            text-align: center;
          }

          .loading-spinner {
            width: 32px;
            height: 32px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .error-icon, .no-data-icon {
            font-size: 48px;
            margin-bottom: 16px;
          }

          .retry-button {
            background: #007bff;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            margin-top: 12px;
            font-weight: 500;
            transition: background-color 0.2s;
          }

          .retry-button:hover {
            background: #0056b3;
          }

          .audience-summary {
            margin-bottom: 20px;
          }

          .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 12px;
          }

          .summary-card {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 16px;
            text-align: center;
          }

          .summary-value {
            font-size: 18px;
            font-weight: 600;
            color: #212529;
            margin-bottom: 4px;
          }

          .summary-label {
            font-size: 12px;
            color: #6c757d;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .audience-table-container {
            overflow-x: auto;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            background: white;
          }

          .audience-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
            min-width: 1200px;
          }

          .audience-table th {
            background: #f8f9fa;
            border-bottom: 2px solid #dee2e6;
            padding: 12px 8px;
            text-align: left;
            font-weight: 600;
            font-size: 12px;
            color: #495057;
            white-space: nowrap;
          }

          .audience-table td {
            padding: 12px 8px;
            border-bottom: 1px solid #f1f3f4;
            vertical-align: middle;
          }

          .audience-table-row:hover {
            background-color: #f8f9fa;
          }

          .audience-name-column { width: 250px; }
          .audience-status-column { width: 100px; }
          .audience-provider-column { width: 120px; }
          .audience-persona-column { width: 140px; }
          .audience-strategy-column { width: 100px; }
          .audience-reach-column { width: 140px; }
          .audience-unique-users-column { width: 140px; }
          .audience-impressions-column { width: 120px; }
          .audience-ecpm-column { width: 80px; }
          .audience-spend-column { width: 140px; }
          .audience-deals-column { width: 150px; }

          .audience-name-content {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .audience-name {
            font-weight: 600;
            color: #212529;
            line-height: 1.3;
          }

          .audience-account {
            font-size: 11px;
            color: #6c757d;
            font-weight: 500;
          }

          .audience-status-badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            white-space: nowrap;
          }

          .audience-status-badge.status-success {
            background: #d4edda;
            color: #155724;
          }

          .audience-status-badge.status-pending {
            background: #fff3cd;
            color: #856404;
          }

          .audience-status-badge.status-error {
            background: #f8d7da;
            color: #721c24;
          }

          .audience-status-badge.status-unknown {
            background: #e2e3e5;
            color: #6c757d;
          }

          .provider-content {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .provider-name {
            font-weight: 500;
            color: #212529;
            font-size: 12px;
          }

          .data-source {
            font-size: 10px;
            color: #6c757d;
            text-transform: uppercase;
            font-weight: 600;
          }

          .reach-content,
          .spend-content {
            display: flex;
            flex-direction: column;
            gap: 1px;
          }

          .reach-primary,
          .spend-primary {
            font-weight: 600;
            color: #212529;
            font-size: 13px;
          }

          .reach-secondary,
          .spend-secondary {
            font-size: 10px;
            color: #6c757d;
            font-weight: 500;
          }

          .report-months-inline {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .months-count {
            font-weight: 600;
            color: #212529;
            font-size: 12px;
          }

          .months-list {
            display: flex;
            flex-wrap: wrap;
            gap: 2px;
          }

          .month-badge {
            background: #e8f5e8;
            color: #2e7d32;
            padding: 1px 4px;
            border-radius: 3px;
            font-size: 9px;
            font-weight: 500;
          }

          .more-months {
            font-size: 10px;
            color: #6c757d;
            font-weight: 500;
          }

          .audience-status-cell,
          .audience-reach-cell,
          .audience-unique-users-cell,
          .audience-impressions-cell,
          .audience-ecpm-cell,
          .audience-spend-cell {
            text-align: center;
          }

          .metric-grid-5 {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 8px;
          }

          .metric-grid-4 {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }

          .metric-grid-3 {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
          }

          .metric-card {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            text-align: center;
          }

          .metric-value {
            font-size: 16px;
            font-weight: 600;
            color: #212529;
            margin-bottom: 4px;
            line-height: 1.2;
          }

          .metric-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .metric-group {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 14px;
          }

          .group-title {
            font-size: 13px;
            font-weight: 600;
            color: #495057;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .metric-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6px 0;
            border-bottom: 1px solid #f1f3f4;
            font-size: 13px;
          }

          .metric-row:last-child {
            border-bottom: none;
          }

          .metric-row span:first-child {
            color: #6c757d;
            font-weight: 500;
          }

          .metric-row span:last-child {
            color: #212529;
            font-weight: 600;
          }

          .device-card {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px;
            text-align: center;
          }

          .device-icon {
            font-size: 20px;
            margin-bottom: 6px;
          }

          .device-count {
            font-size: 14px;
            font-weight: 600;
            color: #212529;
            margin-bottom: 2px;
          }

          .device-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 500;
            text-transform: uppercase;
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .info-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 10px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
          }

          .info-item.full {
            grid-column: 1 / -1;
          }

          .info-label {
            font-size: 11px;
            color: #6c757d;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .info-value {
            font-size: 13px;
            color: #212529;
            font-weight: 500;
            word-break: break-word;
          }

          .info-value.code {
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 11px;
            background: #f1f3f4;
            padding: 2px 6px;
            border-radius: 3px;
          }

          .description {
            grid-column: 1 / -1;
            margin-top: 8px;
            padding: 12px;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
          }

          .description p {
            margin: 6px 0 0 0;
            font-size: 13px;
            color: #495057;
            line-height: 1.4;
          }

          .tag-list {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-top: 4px;
          }

          .tag {
            background: #e8f5e8;
            color: #2e7d32;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 500;
          }

          @media (max-width: 1024px) {
            .modal-container {
              width: 95vw;
              height: 85vh;
            }

            .metric-grid-4 {
              grid-template-columns: repeat(2, 1fr);
            }

            .metric-grid-3 {
              grid-template-columns: 1fr;
            }

            .info-grid {
              grid-template-columns: 1fr;
            }

            .summary-grid {
              grid-template-columns: repeat(3, 1fr);
            }

            .audience-table {
              min-width: 1000px;
              font-size: 12px;
            }

            .audience-table th,
            .audience-table td {
              padding: 10px 6px;
            }
          }

          @media (max-width: 768px) {
            .modal-container {
              width: 98vw;
              height: 90vh;
            }

            .metric-grid-4 {
              grid-template-columns: 1fr;
            }

            .tab-content {
              padding: 16px 20px;
            }

            .summary-grid {
              grid-template-columns: repeat(2, 1fr);
            }

            .tab-button {
              padding: 12px 8px;
              font-size: 12px;
            }

            .tab-icon {
              font-size: 14px;
            }

            .audience-table {
              min-width: 900px;
              font-size: 11px;
            }

            .audience-table th,
            .audience-table td {
              padding: 8px 4px;
            }
          }
        `}</style>
			</div>
		</div>
	);
};
